import { useEffect, useMemo, useState } from "react";
import {
  Mic,
  Play,
  Square,
  Loader2,
  Volume2,
  Download,
  AlertTriangle,
  Wand2,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";
import TaskBanner from "../components/TaskBanner.jsx";
import GenerationProgress from "../components/GenerationProgress.jsx";
import {
  TTS_SUPPORTED,
  getVoices,
  speak,
  cancelSpeech,
} from "../lib/webSpeech.js";
import { ttsServer, getModels } from "../lib/api.js";
import { usePendingTask, useHomeTaskDone } from "../hooks/usePendingTask.js";
import { resolvePromptText } from "../lib/promptText.js";
import { useI18n } from "../lib/i18n.jsx";
import SpeechToTextButton, {
  appendTranscript,
} from "../components/SpeechToTextButton.jsx";

const PRESETS = [
  "Welcome to The Consensus A.I. — your all-in-one AI studio.",
  "Hey there! Just a friendly reminder to drink some water.",
  "In today's video, we're going to learn about neural networks.",
  "Once upon a time, in a quiet forest just past midnight…",
];

export default function TextToSpeech() {
  const { t } = useI18n();
  const [text, setText] = useState(PRESETS[0]);
  const [engine, setEngine] = useState("browser"); // 'browser' | 'nvidia'

  // Browser engine state
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState("");
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [speaking, setSpeaking] = useState(false);

  // NVIDIA engine state
  const [nvModel, setNvModel] = useState("nvidia/magpie-tts-multilingual");
  const [nvVoice, setNvVoice] = useState("Magpie-Multilingual.EN-US.Aria");
  const [nvModels, setNvModels] = useState([]);
  const [nvLoading, setNvLoading] = useState(false);
  const [nvStatus, setNvStatus] = useState("");
  const [nvAudio, setNvAudio] = useState(null);
  const [nvAudioMime, setNvAudioMime] = useState("audio/wav");
  const [nvUsedFallback, setNvUsedFallback] = useState(false);
  const [nvError, setNvError] = useState(null);
  const [taskBanner, setTaskBanner] = useState(null);

  useEffect(() => {
    getVoices().then((v) => {
      setVoices(v);
      const en = v.find((x) => x.lang?.startsWith("en")) || v[0];
      if (en) setVoiceURI(en.voiceURI);
    });
    getModels()
      .then((m) => setNvModels(m.tts || []))
      .catch(() => {});
  }, []);

  const selectedVoice = useMemo(
    () => voices.find((v) => v.voiceURI === voiceURI),
    [voices, voiceURI]
  );

  const playBrowser = async () => {
    if (!text.trim()) return;
    setSpeaking(true);
    try {
      await speak(text, { voice: selectedVoice, rate, pitch });
    } catch (e) {
      console.error(e);
    } finally {
      setSpeaking(false);
    }
  };

  const stopBrowser = () => {
    cancelSpeech();
    setSpeaking(false);
  };

  const generateNvidia = async (textOverride) => {
    const nextText = resolvePromptText(textOverride, text);
    if (!nextText || nvLoading) return;
    setText(nextText);
    setNvLoading(true);
    setNvError(null);
    setNvAudio(null);
    setNvUsedFallback(false);
    setNvStatus(t("common.starting", "Starting…"));
    try {
      const res = await ttsServer(
        {
          model: nvModel,
          text: nextText,
          voice: nvVoice,
          format: "wav",
        },
        { onStatus: setNvStatus }
      );
      setNvAudio(res.audio);
      setNvAudioMime(res.mime || "audio/wav");
      setNvUsedFallback(Boolean(res.fallback));
    } catch (e) {
      setNvError(e.message);
    } finally {
      setNvLoading(false);
      setNvStatus("");
    }
  };

  usePendingTask("/tts", (task) => {
    const spoken = task.prompt.replace(/^read aloud:\s*/i, "").trim() || task.prompt;
    setTaskBanner(task.rawPrompt || task.prompt);
    setEngine("nvidia");
    generateNvidia(spoken);
  });

  const taskDone = useHomeTaskDone(nvLoading, nvError, Boolean(taskBanner));

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader
        icon={Mic}
        title={t("nav.tts", "Text to Speech")}
        subtitle={t(
          "tts.subtitle",
          "Turn text into spoken audio — instantly in your browser, or with NVIDIA's neural voices."
        )}
      />

      <TaskBanner
        label={t("nav.tts", "Text to Speech")}
        prompt={taskBanner}
        running={nvLoading}
        error={nvError}
        completed={taskDone}
      />

      <GenerationProgress
        active={nvLoading && engine === "nvidia"}
        message={nvStatus}
        className="mx-4 md:mx-10 mt-3"
      />

      <div className="flex-1 px-4 md:px-10 py-6 grid lg:grid-cols-[1fr,380px] gap-6 max-w-6xl mx-auto w-full">
        {/* Left: editor */}
        <section className="card space-y-4">
          {!TTS_SUPPORTED && (
            <div className="flex gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {t(
                "tts.noWebSpeech",
                "Your browser doesn't support Web Speech. Use the NVIDIA engine on the right instead."
              )}
            </div>
          )}
          <div>
            <label className="label">{t("common.text", "Text")}</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              className="textarea text-base leading-relaxed"
              placeholder={t(
                "tts.textPlaceholder",
                "Type or paste the text you want to hear out loud…"
              )}
            />
            <SpeechToTextButton
              className="mt-2"
              onTranscript={(chunk) =>
                setText((prev) => appendTranscript(prev, chunk))
              }
              onError={(msg) => setNvError(msg)}
            />
            <div className="mt-3 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  onClick={() => setText(p)}
                  className="text-[11px] text-white/60 hover:text-white border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.05] rounded-full px-2.5 py-1 transition"
                >
                  {p.slice(0, 36)}…
                </button>
              ))}
            </div>
          </div>

          {/* Engine tabs */}
          <div className="flex items-center gap-1 rounded-lg bg-ink-800/80 border border-white/10 p-1 text-xs">
            <button
              onClick={() => setEngine("browser")}
              className={[
                "flex-1 rounded-md px-2 py-1.5 font-medium transition flex items-center gap-1.5 justify-center",
                engine === "browser"
                  ? "bg-consensus-500 text-white"
                  : "text-white/60 hover:text-white",
              ].join(" ")}
            >
              <Volume2 className="h-3.5 w-3.5" />
              {t("tts.browserVoice", "Browser voice (instant)")}
            </button>
            <button
              onClick={() => setEngine("nvidia")}
              className={[
                "flex-1 rounded-md px-2 py-1.5 font-medium transition flex items-center gap-1.5 justify-center",
                engine === "nvidia"
                  ? "bg-consensus-500 text-white"
                  : "text-white/60 hover:text-white",
              ].join(" ")}
            >
              <Wand2 className="h-3.5 w-3.5" />
              {t("tts.nvidiaVoice", "NVIDIA neural (downloadable)")}
            </button>
          </div>

          {engine === "browser" ? (
            <div className="flex gap-2">
              {speaking ? (
                <button onClick={stopBrowser} className="btn-secondary flex-1">
                  <Square className="h-4 w-4 fill-current" />
                  {t("common.stop", "Stop")}
                </button>
              ) : (
                <button
                  onClick={playBrowser}
                  disabled={!text.trim() || !TTS_SUPPORTED}
                  className="btn-primary flex-1"
                >
                  <Play className="h-4 w-4" />
                  {t("tts.speak", "Speak")}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={() => generateNvidia()}
                disabled={nvLoading || !text.trim()}
                className="btn-primary w-full"
              >
                {nvLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("tts.synthesizing", "Synthesizing…")}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    {t("tts.generateAudio", "Generate audio")}
                  </>
                )}
              </button>
              {nvError && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{nvError}</span>
                </div>
              )}
              {nvAudio && (
                <div className="space-y-2">
                  {nvUsedFallback && (
                    <p className="text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5">
                      NVIDIA Magpie TTS is temporarily unavailable, so this clip used the built-in fallback voice engine instead.
                    </p>
                  )}
                  <audio src={nvAudio} controls className="w-full" />
                  <a
                    href={nvAudio}
                    download={`consensus-tts-${Date.now()}.${
                      nvAudioMime.includes("mpeg") || nvAudioMime.includes("mp3")
                        ? "mp3"
                        : "wav"
                    }`}
                    className="btn-secondary w-full justify-center"
                  >
                    <Download className="h-4 w-4" />
                    {nvAudioMime.includes("mpeg") || nvAudioMime.includes("mp3")
                      ? t("tts.downloadAudio", "Download audio")
                      : t("tts.downloadWav", "Download WAV")}
                  </a>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Right: voice + parameters */}
        <aside className="space-y-4">
          {engine === "browser" ? (
            <div className="card space-y-4">
              <div>
                <label className="label">{t("tts.voice", "Voice")}</label>
                <select
                  value={voiceURI}
                  onChange={(e) => setVoiceURI(e.target.value)}
                  className="input"
                  disabled={!TTS_SUPPORTED}
                >
                  {voices.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                  {!voices.length && (
                    <option>{t("tts.loadingVoices", "(loading voices…)")}</option>
                  )}
                </select>
                <p className="text-[10px] text-white/40 mt-1.5">
                  {t(
                    "tts.voiceNote",
                    "Voices come from your OS. macOS & iOS have great built-in natural voices."
                  )}
                </p>
              </div>
              <div>
                <label className="label flex items-center justify-between">
                  <span>{t("tts.rate", "Rate")}</span>
                  <span className="text-white/60 normal-case tracking-normal font-normal">
                    {Number(rate).toFixed(2)}×
                  </span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full accent-consensus-500"
                />
              </div>
              <div>
                <label className="label flex items-center justify-between">
                  <span>{t("tts.pitch", "Pitch")}</span>
                  <span className="text-white/60 normal-case tracking-normal font-normal">
                    {Number(pitch).toFixed(2)}
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.05"
                  value={pitch}
                  onChange={(e) => setPitch(Number(e.target.value))}
                  className="w-full accent-consensus-500"
                />
              </div>
            </div>
          ) : (
            <div className="card space-y-4">
              <div>
              <label className="label">{t("common.model", "Model")}</label>
                <select
                  value={nvModel}
                  onChange={(e) => setNvModel(e.target.value)}
                  className="input"
                >
                  {nvModels.length === 0 && <option value={nvModel}>{nvModel}</option>}
                  {nvModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">{t("tts.voice", "Voice")}</label>
                <input
                  value={nvVoice}
                  onChange={(e) => setNvVoice(e.target.value)}
                  className="input"
                  placeholder={t("tts.defaultVoice", "default")}
                />
                <SpeechToTextButton
                  className="mt-2"
                  onTranscript={(chunk) =>
                    setNvVoice((prev) => appendTranscript(prev, chunk))
                  }
                  onError={(msg) => setNvError(msg)}
                />
                <p className="text-[10px] text-white/40 mt-1.5">
                  {t("tts.tryVoicesPrefix", "Try")}{" "}
                  <code>Magpie-Multilingual.EN-US.Aria</code> or{" "}
                  <code>Magpie-Multilingual.EN-US.Sofia</code>. See voices on{" "}
                  <a
                    href="https://build.nvidia.com/nvidia/magpie-tts-multilingual"
                    target="_blank"
                    rel="noreferrer"
                    className="text-consensus-400 hover:underline"
                  >
                    build.nvidia.com
                  </a>
                  .
                </p>
              </div>
              <p className="text-[10px] text-white/40 leading-relaxed">
                {t(
                  "tts.nvidiaNote",
                  "NVIDIA neural TTS may not be enabled on every account. If generation fails, use the browser voice — it works everywhere."
                )}
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
